/* eslint-disable no-console */
import {
  takeLatest,
  takeEvery,
  put,
  call,
  delay,
  select,
  fork,
  cancel,
  cancelled,
} from 'redux-saga/effects';
import { message } from 'antd';
import { get } from 'lodash';
import {
  FETCH_ALL_HUBS,
  SET_PATHWAYU_INTEGRATION,
  CREATE_INTEGRATION,
  INTEGRATION_SUCCESS,
  FETCH_ALL_COMPANIES,
  FETCH_MIGRATION_STATUS,
  START_MIGRATION_POLLING,
  STOP_MIGRATION_POLLING,
  CLEAR_FAILED_MIGRATION,
} from './actions';
import pathwayuIntegrationAPIs from '../../api/pathwayuIntegration';
import { normalizePathwayUCompanyId } from './util';

// Store the polling task reference
let pollingTask = null;

function* fetchAllHubs() {
  try {
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { loading: true },
    });

    // Fetch hubs (clusters will be populated by backend)
    const { data: hubs } = yield call(pathwayuIntegrationAPIs.getAllHubs);

    // Add keys for table rendering
    const hubsWithKeys = hubs.map(hub => ({
      ...hub,
      key: hub.id,
    }));

    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        hubs: hubsWithKeys,
        loading: false,
      },
    });

    // Check if there are any active migrations and start polling if needed
    const hasActiveMigrations = hubsWithKeys.some(
      hub =>
        hub.pathwayUMigration &&
        hub.pathwayUMigration.status !== 'completed' &&
        hub.pathwayUMigration.status !== 'failed'
    );

    if (hasActiveMigrations) {
      yield put({ type: START_MIGRATION_POLLING });
    }
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      error.message ||
      'Failed to fetch hubs';
    message.error(msg);
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        loading: false,
        error: msg,
      },
    });
    console.error('Error fetching hubs:', error);
  }
}

function* fetchAllCompanies() {
  try {
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { companiesLoading: true },
    });
    const { data: companies } = yield call(
      pathwayuIntegrationAPIs.getPathwayUCompanies
    );

    // Get current hubs to check which companies are already integrated
    const state = yield select();
    const hubs = get(state, 'pathwayuIntegration.hubs', []);

    // Create a set of company IDs that are already integrated
    const integratedCompanyIds = new Set();
    hubs.forEach(hub => {
      const companyId = normalizePathwayUCompanyId(
        hub.pathwayUIntegration?.pathwayUCompanyId
      );
      if (companyId) {
        integratedCompanyIds.add(companyId);
      }
    });

    // Mark companies as integrated if they are already used by any hub
    const companiesWithIntegrationStatus = companies.map(company => {
      const companyId = normalizePathwayUCompanyId(company.id);
      return {
        ...company,
        integrated: companyId != null && integratedCompanyIds.has(companyId),
      };
    });

    // Sort companies alphabetically by name
    const sortedCompanies = companiesWithIntegrationStatus.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { companies: sortedCompanies, companiesLoading: false },
    });
  } catch (error) {
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { companiesLoading: false },
    });
    console.error('Error fetching companies:', error);
  }
}

function* createIntegration({ data }) {
  try {
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { integrationLoading: true },
    });

    const response = yield call(
      pathwayuIntegrationAPIs.createIntegration,
      data
    );

    message.success('PathwayU migration job created successfully');

    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        integrationLoading: false,
      },
    });

    // Refresh hubs list
    yield put({ type: FETCH_ALL_HUBS });

    // Dispatch success action
    yield put({ type: INTEGRATION_SUCCESS });

    return response.data;
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      error.message ||
      'Failed to create integration';
    message.error(msg);
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        integrationLoading: false,
        error: msg,
      },
    });
    throw error;
  }
}

function* fetchMigrationStatus({ hubId }) {
  try {
    const { data: migrationStatus } = yield call(
      pathwayuIntegrationAPIs.getMigrationStatus,
      hubId
    );

    // Update the hub's migration status in the store
    const state = yield select();
    const hubs = get(state, 'pathwayuIntegration.hubs', []);

    const updatedHubs = hubs.map(hub => {
      if (hub.id === parseInt(hubId, 10) || hub.id === hubId) {
        return {
          ...hub,
          pathwayUMigration: migrationStatus.hasActiveMigration
            ? {
                jobId: migrationStatus.jobId,
                status: migrationStatus.status,
                progress: migrationStatus.progress,
                errorMessage: migrationStatus.errorMessage,
                createdAt: migrationStatus.createdAt,
              }
            : null,
        };
      }
      return hub;
    });

    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { hubs: updatedHubs },
    });

    return migrationStatus;
  } catch (error) {
    console.error('Error fetching migration status:', error);
    return null;
  }
}

function* fetchAllMigrationStatuses() {
  try {
    const state = yield select();
    const hubs = get(state, 'pathwayuIntegration.hubs', []);

    // Fetch migration status for all hubs that have active migrations
    const hubsWithActiveMigrations = hubs.filter(
      hub =>
        hub.pathwayUMigration &&
        hub.pathwayUMigration.status !== 'completed' &&
        hub.pathwayUMigration.status !== 'failed'
    );

    if (hubsWithActiveMigrations.length === 0) {
      console.log('No active migrations found');
      return false;
    }

    console.log(
      `Fetching migration status for ${hubsWithActiveMigrations.length} hubs`
    );

    // Fetch status for each hub with active migration
    for (const hub of hubsWithActiveMigrations) {
      yield call(fetchMigrationStatus, { hubId: hub.id });
    }

    return true;
  } catch (error) {
    console.error('Error fetching all migration statuses:', error);
    return false;
  }
}

function* pollMigrationStatus() {
  try {
    // First, fetch status immediately on mount
    console.log('Starting migration polling - fetching initial status');
    const hasActiveMigrations = yield call(fetchAllMigrationStatuses);

    if (!hasActiveMigrations) {
      console.log('No active migrations found, stopping polling');
      return;
    }

    // Then poll every 20 seconds
    while (true) {
      // Wait 20 seconds before next polling cycle
      yield delay(20000);

      const state = yield select();
      const hubs = get(state, 'pathwayuIntegration.hubs', []);

      // Check if there are still active migrations
      const hubsWithActiveMigrations = hubs.filter(
        hub =>
          hub.pathwayUMigration &&
          hub.pathwayUMigration.status !== 'completed' &&
          hub.pathwayUMigration.status !== 'failed'
      );

      // If no active migrations, stop polling
      if (hubsWithActiveMigrations.length === 0) {
        console.log('No active migrations found, stopping polling');
        break;
      }

      console.log(
        `Polling migration status for ${hubsWithActiveMigrations.length} hubs`
      );

      // Fetch status for each hub with active migration
      yield call(fetchAllMigrationStatuses);
    }
  } catch (error) {
    console.error('Error in migration status polling:', error);
  } finally {
    if (yield cancelled()) {
      console.log('Migration polling was cancelled');
    }
  }
}

function* startMigrationPolling() {
  // Cancel any existing polling task
  if (pollingTask) {
    yield cancel(pollingTask);
    pollingTask = null;
  }

  // Start a new polling task
  pollingTask = yield fork(pollMigrationStatus);
}

function* stopMigrationPolling() {
  if (pollingTask) {
    yield cancel(pollingTask);
    pollingTask = null;
    console.log('Migration polling stopped');
  }
}

function* clearFailedMigration({ hubId }) {
  try {
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: { clearLoading: true },
    });

    const response = yield call(
      pathwayuIntegrationAPIs.clearFailedMigration,
      hubId
    );

    message.success('Failed migration cleared successfully');

    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        clearLoading: false,
      },
    });

    // Refresh hubs list to get updated migration status
    yield put({ type: FETCH_ALL_HUBS });

    return response.data;
  } catch (error) {
    const msg =
      get(error, 'response.data.message') ||
      error.message ||
      'Failed to clear failed migration';
    message.error(msg);
    yield put({
      type: SET_PATHWAYU_INTEGRATION,
      data: {
        clearLoading: false,
        error: msg,
      },
    });
    throw error;
  }
}

const sagas = [
  takeLatest(FETCH_ALL_HUBS, fetchAllHubs),
  takeEvery(FETCH_ALL_COMPANIES, fetchAllCompanies),
  takeLatest(CREATE_INTEGRATION, createIntegration),
  takeLatest(FETCH_MIGRATION_STATUS, fetchMigrationStatus),
  takeLatest(START_MIGRATION_POLLING, startMigrationPolling),
  takeLatest(STOP_MIGRATION_POLLING, stopMigrationPolling),
  takeLatest(CLEAR_FAILED_MIGRATION, clearFailedMigration),
];

export default sagas;
